import { el } from './dom';

export interface TabDef {
  id: string;
  label: string;
  /** Lazily builds the tab's content the first time it is shown. */
  render: () => HTMLElement;
}

/** Accessible tab strip with lazy, cached content panels. */
export function createTabs(tabs: TabDef[]): HTMLElement {
  const cache = new Map<string, HTMLElement>();
  const panel = el('div', { class: 'tab-panel' });
  const buttons = new Map<string, HTMLButtonElement>();

  function activate(id: string): void {
    for (const [tid, btn] of buttons) {
      const selected = tid === id;
      btn.classList.toggle('active', selected);
      btn.setAttribute('aria-selected', selected ? 'true' : 'false');
      btn.tabIndex = selected ? 0 : -1;
    }
    let content = cache.get(id);
    if (!content) {
      content = tabs.find((t) => t.id === id)!.render();
      cache.set(id, content);
    }
    panel.replaceChildren(content);
  }

  const strip = el('div', { class: 'tabs', role: 'tablist' });
  for (const tab of tabs) {
    const btn = el(
      'button',
      {
        type: 'button',
        class: 'tab',
        role: 'tab',
        id: `tab-${tab.id}`,
        'aria-controls': 'tab-panel',
      },
      [tab.label],
    );
    btn.addEventListener('click', () => activate(tab.id));
    buttons.set(tab.id, btn);
    strip.append(btn);
  }
  panel.id = 'tab-panel';
  panel.setAttribute('role', 'tabpanel');

  const root = el('div', { class: 'tabs-root' }, [strip, panel]);
  if (tabs.length > 0) activate(tabs[0].id);
  return root;
}
